import { WarningFilled } from '@ant-design/icons';
import { Row, Space, Col, notification } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';

import { Button, Input, Modal } from '..';

import { disabledDateAfterToday } from '../../utils/utils';
import { DatePicker, DatePickerCustomProps } from './datepicker/DatePicker';

type ModalStopoverOmissionProps = {
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
  vesselName: string;
  stopoverId: number | undefined;
  isOmitted?: boolean;
  omissionMotivation?: string;
  omissionDate?: string;
  omissionConfirm: (
    id: number,
    isOmitted: boolean,
    motivation: string,
    omissionDate: string
  ) => void | Promise<void>;
  isLoading?: boolean;
};

export function ModalStopoverOmission(props: ModalStopoverOmissionProps) {
  const {
    showModal,
    setShowModal,
    vesselName,
    stopoverId,
    isOmitted,
    omissionMotivation,
    omissionDate,
    omissionConfirm,
    isLoading,
  } = props;
  const [motivation, setMotivation] = useState('');
  const [date, setDate] = useState<moment.Moment | null>(null);

  useEffect(() => {
    if (showModal) {
      setMotivation(omissionMotivation || '');
      // Escala já omitida abre com a data registrada; nova omissão abre com a
      // data e hora atuais.
      setDate(omissionDate ? moment(omissionDate) : moment());
    }
  }, [showModal, omissionMotivation, omissionDate]);

  function handleConfirmOmission() {
    if (!motivation.trim()) {
      notification.warning({
        message: 'Informe o motivo da omissão',
        placement: 'top',
        duration: 3,
      });
      return;
    }
    if (!date) {
      notification.warning({
        message: 'Informe a data e hora da omissão',
        placement: 'top',
        duration: 3,
      });
      return;
    }
    if (stopoverId) {
      omissionConfirm(
        stopoverId,
        true,
        motivation.trim(),
        date.format('YYYY-MM-DD HH:mm')
      );
    }
    handleCloseModal();
  }

  function handleCloseModal() {
    setMotivation('');
    setDate(null);
    setShowModal(false);
  }

  return (
    <Modal
      visible={showModal}
      className="TOSWarningModal"
      width={520}
      title={
        <>
          <WarningFilled /> Atenção
        </>
      }
      onCancel={() => handleCloseModal()}
      footer={null}
    >
      <>
        <p>
          {isOmitted
            ? `Deseja atualizar os dados da omissão de ${vesselName}?`
            : `Tem certeza de que deseja registrar a omissão de ${vesselName}?`}
        </p>
        <Row gutter={24} align="bottom">
          <Col span={24}>
            <div style={{ marginBottom: '4px' }}>Data e hora da omissão</div>
            <DatePicker
              id="omission_date"
              // O wrapper une as props de DatePicker e RangePicker, por isso o
              // cast do valor único.
              value={date as DatePickerCustomProps['value']}
              onChange={(value) => setDate(value as moment.Moment | null)}
              disabledDate={disabledDateAfterToday}
              format="DD/MM/YYYY HH:mm"
              minuteStep={1}
              style={{ width: '100%' }}
            />
          </Col>
        </Row>
        <br />
        <Row gutter={24} align="bottom">
          <Col span={24}>
            <Input.TextArea
              value={motivation}
              required
              onChange={(e) => setMotivation(e.target.value)}
              maxLength={200}
              placeholder="Motivo da omissão"
            />
          </Col>
        </Row>
        <br />
        <Row justify="end">
          <Space size={12}>
            <Button onClick={() => handleCloseModal()}>Voltar</Button>
            <Button
              danger
              loading={isLoading}
              onClick={() => handleConfirmOmission()}
            >
              {isOmitted ? 'Atualizar omissão' : 'Registrar omissão'}
            </Button>
          </Space>
        </Row>
      </>
    </Modal>
  );
}
